import dynamic from 'next/dynamic';

import { useAdDetailsContext } from '../../../../../contexts/AdDetailsContext';

import { Button } from '../../../../../components/Toolkit/Button/Button';
import {
  RevealPhoneNumber,
  type RevealPhoneNumberProps,
} from '../../../../../components/PageWrappers/SharedComponents/RevealPhoneNumber/RevealPhoneNumber';
import { Columns } from '../../../../../components/Toolkit/Columns/Columns';
import { ButtonLink } from '../../../../../components/Toolkit/Button/ButtonLink';
import { EmailDealerOrSeller } from '../EmailDealerOrSeller/EmailDealerOrSeller';
import { DisplayContainer } from '../../../../../components/Support/DisplayContainer/DisplayContainer';
import { ReserveCTA } from '../ReserveCTA/ReserveCTA';
import { Control } from './ctaVariants/Control';
import { Variant1 } from './ctaVariants/Variant1';
import { Variant2 } from './ctaVariants/Variant2';
import { type EmailDealerOrSellerProps } from '../EmailDealerOrSeller/EmailDealerOrSeller.typed';
import getConfig from 'next/config';

import * as Styled from './UserActionsStickyFooter.styled';

type FeatureFlag = 'control' | 'variant1' | 'variant2' | null;

const ContactDealershipBottomSheet = dynamic(
  import('./subcomponents/ContactDealershipBottomSheet').then(
    (mod) => mod.ContactDealershipBottomSheet,
  ),
);

import { type ReservationStatuses } from '../../types';

type UserActionsStickyFooterProps = Omit<
  {
    phoneResponse: boolean;
    sectionName?: string;
    emailResponse: boolean;
    whatsappResponse: boolean;
    isViewingOwnAd?: boolean;
    editAdHref: string;
    handleBoost: () => Promise<void>;
    active: boolean;
    reservationStatus?: ReservationStatuses;
    priceEuro?: number | null;
    // Driven by convert experiment
    ctaVariantsFlag?: FeatureFlag;
  } & EmailDealerOrSellerProps &
    RevealPhoneNumberProps,
  'dealerID' | 'clickSource'
>;

const UserActionsStickyFooter = (props: UserActionsStickyFooterProps) => {
  const {
    adId,
    emailResponse,
    phoneResponse,
    campaignId,
    sectionName,
    sellerType,
    whatsappResponse,
    handleReveal,
    isViewingOwnAd,
    editAdHref,
    handleBoost,
    active,
    reservationStatus,
    priceEuro,
    price,
    ctaVariantsFlag,
  } = props;
  const {
    publicRuntimeConfig: {
      CTA_VARIANTS_TOGGLE,
      FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE,
    },
  } = getConfig();

  const CLICK_SOURCE = 'mobile-sticky';

  const { setIsContactSellerModalOpen } = useAdDetailsContext();

  const ctas = [emailResponse, phoneResponse, !!reservationStatus];
  const ctaCount = ctas.reduce((total: number, option: boolean) => {
    return total + (option ? 1 : 0);
  }, 0);
  /**
   * Up to two CTAs can be visible in the footer at any time.
   *
   * If reservations are available, this option should always be displayed, along with the contact option.
   *
   * If more than one contact option is available, we put all contact options behind the contact dealership CTA.
   */
  const maximumNumberOfVisibleCtasExceeded = ctaCount > 2;

  /**
   * WhatsApp should only be available from the contact dealership option, along with any other contact options.
   */
  const displayContactDealershipBottomSheet =
    maximumNumberOfVisibleCtasExceeded || whatsappResponse;

  const phoneContactProps: RevealPhoneNumberProps = {
    adId,
    clickSource: CLICK_SOURCE,
    isPrimaryButton: !emailResponse,
    campaignId,
    showNumberTextVariant: true,
    sectionName,
    sellerType,
    id: 'phone-number-button-footer',
    price,
  };

  const emailSellerProps: EmailDealerOrSellerProps = {
    adId,
    clickSource: CLICK_SOURCE,
    dealerID: 'dealer-enquiry-btn-footer',
    handleReveal,
    sellerType,
  };

  return (
    <>
      <Styled.MobileAndTabletStickyFooter
        position="fixed"
        active={FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE ? true : active}
        data-testid="mobile-sticky-cta"
      >
        {isViewingOwnAd ? (
          <Columns>
            <Button onClick={handleBoost} data-testid="boost-button">
              Boost
            </Button>
            <ButtonLink href={editAdHref} ofType="SECONDARY">
              Edit
            </ButtonLink>
          </Columns>
        ) : (
          <>
            {/**
             * WhatsApp should only be visible on mobile and behind the contact dealership button.
             *
             * If WhatsApp is not available or the screen size is not small,
             * we omit WhatsApp as an option and therefore the contact dealership button. */}
            <DisplayContainer
              config={{ small: 'block', medium: 'none', large: 'none' }}
            >
              {CTA_VARIANTS_TOGGLE ? (
                <>
                  {ctaVariantsFlag === 'variant1' ? (
                    <Variant1
                      displayContactDealershipBottomSheet={
                        displayContactDealershipBottomSheet
                      }
                      phoneResponse={phoneResponse}
                      phoneContactProps={{
                        ...phoneContactProps,
                        isPrimaryButton: true,
                      }}
                      emailResponse={emailResponse}
                      emailSellerProps={emailSellerProps}
                      whatsappResponse={whatsappResponse}
                      adId={adId}
                      priceEuro={priceEuro}
                      sellerType={sellerType}
                      sectionName={sectionName}
                      price={price}
                    />
                  ) : ctaVariantsFlag === 'variant2' ? (
                    <Variant2
                      phoneResponse={phoneResponse}
                      phoneContactProps={{
                        ...phoneContactProps,
                        isPrimaryButton: true,
                      }}
                      emailResponse={emailResponse}
                      emailSellerProps={emailSellerProps}
                      whatsappResponse={whatsappResponse}
                      adId={adId}
                      priceEuro={priceEuro}
                      sellerType={sellerType}
                      sectionName={sectionName}
                      price={price}
                    />
                  ) : (
                    <Control
                      reservationStatus={reservationStatus}
                      displayContactDealershipBottomSheet={
                        displayContactDealershipBottomSheet
                      }
                      setIsContactSellerModalOpen={setIsContactSellerModalOpen}
                      phoneResponse={phoneResponse}
                      phoneContactProps={phoneContactProps}
                      emailResponse={emailResponse}
                      emailSellerProps={emailSellerProps}
                    />
                  )}
                </>
              ) : (
                <Control
                  reservationStatus={reservationStatus}
                  displayContactDealershipBottomSheet={
                    displayContactDealershipBottomSheet
                  }
                  setIsContactSellerModalOpen={setIsContactSellerModalOpen}
                  phoneResponse={phoneResponse}
                  phoneContactProps={phoneContactProps}
                  emailResponse={emailResponse}
                  emailSellerProps={emailSellerProps}
                />
              )}
            </DisplayContainer>
            <DisplayContainer
              config={{ small: 'none', medium: 'block', large: 'none' }}
            >
              <Columns>
                {reservationStatus && (
                  <ReserveCTA reservationStatus={reservationStatus} />
                )}
                {maximumNumberOfVisibleCtasExceeded && (
                  <Button onClick={() => setIsContactSellerModalOpen(true)}>
                    Contact Dealership
                  </Button>
                )}
                {!maximumNumberOfVisibleCtasExceeded && phoneResponse && (
                  <RevealPhoneNumber {...phoneContactProps} />
                )}
                {!maximumNumberOfVisibleCtasExceeded && emailResponse && (
                  <EmailDealerOrSeller {...emailSellerProps} />
                )}
              </Columns>
            </DisplayContainer>
          </>
        )}
      </Styled.MobileAndTabletStickyFooter>
      {displayContactDealershipBottomSheet && (
        <ContactDealershipBottomSheet
          adId={adId}
          emailResponse={emailResponse}
          phoneResponse={phoneResponse}
          sectionName={sectionName}
          campaignId={campaignId}
          handleReveal={handleReveal}
          whatsappResponse={whatsappResponse}
          priceEuro={priceEuro}
          sellerType={sellerType}
          price={price}
        />
      )}
    </>
  );
};

export { UserActionsStickyFooter, type UserActionsStickyFooterProps };
